import { useState, type FormEvent } from "react";

type Step = "password" | "email" | "otp";

interface OtpLoginFormsProps {
  step: Step;
  email: string;
  password: string;
  otp: string;
  loading: boolean;
  error: string | null;
  successMsg: string | null;
  devOtp: string | null;
  onEmailChange: (v: string) => void;
  onPasswordChange: (v: string) => void;
  onOtpChange: (v: string) => void;
  onPasswordLogin: (e: FormEvent) => void;
  onSendOtp: (e: FormEvent) => void;
  onVerifyOtp: (e: FormEvent) => void;
  onBack: () => void;
  onUseOtp: () => void;
  onUsePassword: () => void;
  onRequestAccess: () => void;
}

export function OtpLoginForms({
  step,
  email,
  password,
  otp,
  loading,
  error,
  successMsg,
  devOtp,
  onEmailChange,
  onPasswordChange,
  onOtpChange,
  onPasswordLogin,
  onSendOtp,
  onVerifyOtp,
  onBack,
  onUseOtp,
  onUsePassword,
  onRequestAccess,
}: OtpLoginFormsProps) {
  const [showPassword, setShowPassword] = useState(false);

  return (
    <div className="max-w-sm w-full mx-auto">
      <div className="mb-6">
        <h2 className="text-xl sm:text-2xl font-bold text-saas-text">
          {step === "otp" ? "Check your email" : "Welcome back"}
        </h2>
        <p className="text-sm text-saas-muted mt-1">
          {step === "otp" ? (
            <>
              We sent a 6-digit code to{" "}
              <span className="font-medium text-saas-text">{email}</span>
            </>
          ) : (
            "Sign in to your KSP Traders account"
          )}
        </p>
      </div>

      {step !== "otp" && (
        <div className="grid grid-cols-2 gap-2 mb-6 rounded-lg bg-gray-100 p-1">
          <button
            type="button"
            onClick={onUsePassword}
            className={`rounded-md px-3 py-2 text-sm font-semibold transition-colors ${
              step === "password"
                ? "bg-white text-saas-text shadow-sm"
                : "text-saas-muted hover:text-saas-text"
            }`}
          >
            Password
          </button>
          <button
            type="button"
            onClick={onUseOtp}
            className={`rounded-md px-3 py-2 text-sm font-semibold transition-colors ${
              step === "email"
                ? "bg-white text-saas-text shadow-sm"
                : "text-saas-muted hover:text-saas-text"
            }`}
          >
            OTP
          </button>
        </div>
      )}

      {step !== "password" && (
        <div className="flex items-center gap-2 mb-6">
          <div
            className={`flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold transition-colors ${
              step === "email"
                ? "bg-brand-600 text-white"
                : "bg-green-100 text-green-700"
            }`}
          >
            {step === "otp" && (
              <svg
                className="w-3 h-3"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2.5}
                  d="M5 13l4 4L19 7"
                />
              </svg>
            )}
            {step === "email" ? "1" : ""} Email
          </div>
          <div className="flex-1 h-px bg-saas-border" />
          <div
            className={`flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold transition-colors ${
              step === "otp"
                ? "bg-brand-600 text-white"
                : "bg-gray-100 text-gray-400"
            }`}
          >
            2 Verify OTP
          </div>
        </div>
      )}

      {successMsg && (
        <div className="mb-4 flex items-start gap-2.5 p-3 bg-green-50 border border-green-200 rounded-xl">
          <svg
            className="w-4 h-4 text-green-600 mt-0.5 shrink-0"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          <p className="text-xs sm:text-sm text-green-700">{successMsg}</p>
        </div>
      )}

      {error && (
        <div className="mb-4 flex items-start gap-2.5 p-3 bg-red-50 border border-red-200 rounded-xl">
          <svg
            className="w-4 h-4 text-red-500 mt-0.5 shrink-0"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          <p className="text-xs sm:text-sm text-red-700">{error}</p>
        </div>
      )}

      {step === "password" && (
        <form
          onSubmit={(e) => void onPasswordLogin(e)}
          className="space-y-4"
          autoComplete="off"
        >
          <div>
            <label
              htmlFor="login-email"
              className="block text-sm font-medium text-saas-text mb-1.5"
            >
              Email Address
            </label>
            <input
              id="login-email"
              name="login_email_manual"
              type="email"
              autoComplete="off"
              autoFocus
              required
              value={email}
              onChange={(e) => onEmailChange(e.target.value)}
              placeholder="you@company.com"
              className="input"
              disabled={loading}
            />
          </div>
          <div>
            <label
              htmlFor="login-password"
              className="block text-sm font-medium text-saas-text mb-1.5"
            >
              Password
            </label>
            <div className="relative">
              <input
                id="login-password"
                name="login_password_manual"
                type={showPassword ? "text" : "password"}
                autoComplete="new-password"
                required
                minLength={8}
                value={password}
                onChange={(e) => onPasswordChange(e.target.value)}
                placeholder="Enter your password"
                className="input pr-11"
                disabled={loading}
              />
              <button
                type="button"
                onClick={() => setShowPassword((value) => !value)}
                className="absolute inset-y-0 right-0 flex w-11 items-center justify-center text-saas-muted hover:text-saas-text disabled:cursor-not-allowed disabled:opacity-50"
                disabled={loading}
                aria-label={showPassword ? "Hide password" : "Show password"}
              >
                {showPassword ? (
                  <svg
                    className="h-5 w-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    aria-hidden="true"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M3 3l18 18M10.584 10.587a2 2 0 002.828 2.826M9.88 4.24A10.44 10.44 0 0112 4c5.25 0 9.27 4.13 10.5 8- .42 1.32-1.22 2.73-2.34 3.98M6.61 6.61C4.18 8.18 2.61 10.62 1.5 12c1.23 3.87 5.25 8 10.5 8 1.62 0 3.12-.4 4.43-1.08"
                    />
                  </svg>
                ) : (
                  <svg
                    className="h-5 w-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    aria-hidden="true"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M2.25 12s3.75-7 9.75-7 9.75 7 9.75 7-3.75 7-9.75 7-9.75-7-9.75-7z"
                    />
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 15a3 3 0 100-6 3 3 0 000 6z"
                    />
                  </svg>
                )}
              </button>
            </div>
          </div>
          <button
            type="submit"
            disabled={loading || !email || password.length < 8}
            className="btn-primary w-full"
          >
            {loading ? (
              <>
                <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />{" "}
                Signing in...
              </>
            ) : (
              "Sign In"
            )}
          </button>
        </form>
      )}

      {step === "email" && (
        <form
          onSubmit={(e) => void onSendOtp(e)}
          className="space-y-4"
          autoComplete="off"
        >
          <div>
            <label
              htmlFor="email"
              className="block text-sm font-medium text-saas-text mb-1.5"
            >
              Email Address
            </label>
            <input
              id="otp-email"
              name="otp_email_manual"
              type="email"
              autoComplete="off"
              autoFocus
              required
              value={email}
              onChange={(e) => onEmailChange(e.target.value)}
              placeholder="you@company.com"
              className="input"
              disabled={loading}
            />
            <p className="mt-1.5 text-xs text-saas-muted">
              We&apos;ll send a one-time password to this address.
            </p>
          </div>
          <button
            type="submit"
            disabled={loading || !email}
            className="btn-primary w-full"
          >
            {loading ? (
              <>
                <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />{" "}
                Sending OTP...
              </>
            ) : (
              "Continue"
            )}
          </button>
        </form>
      )}

      {step === "otp" && (
        <form onSubmit={(e) => void onVerifyOtp(e)} className="space-y-4">
          {devOtp && (
            <div className="p-3 bg-amber-50 border border-amber-300 rounded-xl">
              <p className="text-xs font-semibold text-amber-800 mb-0.5">
                DEV MODE - OTP auto-filled
              </p>
              <p className="text-xl font-mono font-bold text-amber-900 tracking-widest">
                {devOtp}
              </p>
            </div>
          )}
          <div>
            <label
              htmlFor="otp"
              className="block text-sm font-medium text-saas-text mb-1.5"
            >
              One-Time Password
            </label>
            <input
              id="otp"
              type="text"
              inputMode="numeric"
              pattern="[0-9]{6}"
              maxLength={6}
              autoFocus
              required
              value={otp}
              onChange={(e) =>
                onOtpChange(e.target.value.replace(/\D/g, "").slice(0, 6))
              }
              placeholder="000000"
              className="input text-center text-2xl tracking-[0.5em] font-mono"
              disabled={loading}
            />
            <p className="mt-1.5 text-xs text-saas-muted">
              Enter the 6-digit code. Valid for 10 minutes.
            </p>
          </div>
          <button
            type="submit"
            disabled={loading || otp.length !== 6}
            className="btn-primary w-full"
          >
            {loading ? (
              <>
                <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />{" "}
                Verifying...
              </>
            ) : (
              "Verify & Sign In"
            )}
          </button>
          <button
            type="button"
            onClick={onBack}
            disabled={loading}
            className="btn-secondary w-full"
          >
            Use password instead
          </button>
        </form>
      )}

      <p className="text-center text-xs text-saas-muted mt-6">
        Don&apos;t have an account?{" "}
        <button
          type="button"
          onClick={onRequestAccess}
          className="text-brand-600 hover:text-brand-700 font-medium hover:underline transition-colors"
        >
          Request access
        </button>
      </p>
    </div>
  );
}
